import type { components } from "../../shared/api/generated/trading-engine.js";

type BacktestChartResource = components["schemas"]["BacktestChart"];
type TickerBarsResource = components["schemas"]["TickerBars"];

import type { ChartBarView, ChartFillView } from "../../shared/charts/chart-models.js";
export type { ChartBarView, ChartFillView } from "../../shared/charts/chart-models.js";

export interface ChartDecisionView {
  readonly action: string;
  readonly barEndedAt: string;
  readonly reason: string;
}

export interface BacktestEvidenceRow {
  readonly action: string;
  readonly at: string;
  readonly decision?: ChartDecisionView;
  readonly fill?: ChartFillView;
  readonly id: string;
}

export interface BacktestChartView {
  readonly range?: { readonly from: string; readonly to: string };
  readonly nextCursor?: string;
  readonly barSource: "artifact" | "reconstructed";
  readonly bars: readonly ChartBarView[];
  readonly decisions: readonly ChartDecisionView[];
  readonly equityCurve: readonly { readonly at: string; readonly equityUsd: string }[];
  readonly fills: readonly ChartFillView[];
  readonly interval: string;
  readonly runId: string;
  readonly symbol: string;
}

export interface TickerBarsView {
  readonly range: { readonly from: string; readonly to: string };
  readonly bars: readonly ChartBarView[];
  readonly dataVersion: string;
  readonly interval: string;
  readonly symbol: string;
}

function chartBar(resource: BacktestChartResource["bars"][number]): ChartBarView {
  return {
    close: resource.close,
    endedAt: resource.endedAt,
    high: resource.high,
    low: resource.low,
    open: resource.open,
    sessionDate: resource.sessionDate,
    sessionPhase: resource.sessionPhase,
    startedAt: resource.startedAt,
    volume: resource.volume,
  };
}

export function backtestChartView(resource: BacktestChartResource): BacktestChartView {
  return {
    ...(resource.range === undefined ? {} : { range: resource.range }),
    ...(resource.nextCursor === undefined ? {} : { nextCursor: resource.nextCursor }),
    barSource: resource.barSource,
    bars: resource.bars.map(chartBar),
    decisions: resource.decisions,
    equityCurve: resource.equityCurve,
    fills: resource.fills,
    interval: resource.interval,
    runId: resource.runId,
    symbol: resource.symbol,
  };
}

export function tickerBarsView(resource: TickerBarsResource): TickerBarsView {
  return {
    range: { from: resource.from, to: resource.to },
    bars: resource.bars.map(chartBar),
    dataVersion: resource.dataVersion,
    interval: resource.interval,
    symbol: resource.symbol,
  };
}

export function backtestEvidenceRows(
  fills: readonly ChartFillView[],
  decisions: readonly ChartDecisionView[],
): readonly BacktestEvidenceRow[] {
  const orderedDecisions = [...decisions].sort((left, right) =>
    left.barEndedAt.localeCompare(right.barEndedAt),
  );
  const used = new Set<number>();
  const rows: BacktestEvidenceRow[] = [...fills]
    .sort((left, right) => left.occurredAt.localeCompare(right.occurredAt))
    .map((fill) => {
      const fillAt = Date.parse(fill.occurredAt);
      const candidates = orderedDecisions
        .map((decision, index) => ({ decision, index, time: Date.parse(decision.barEndedAt) }))
        .filter(({ decision, index }) => decision.action === fill.action && !used.has(index))
        .sort((left, right) => {
          const leftIsFuture = left.time > fillAt ? 1 : 0;
          const rightIsFuture = right.time > fillAt ? 1 : 0;
          return (
            leftIsFuture - rightIsFuture ||
            Math.abs(fillAt - left.time) - Math.abs(fillAt - right.time)
          );
        });
      const match = candidates[0];
      if (match !== undefined) used.add(match.index);
      return {
        action: fill.action,
        at: fill.occurredAt,
        ...(match === undefined ? {} : { decision: match.decision }),
        fill,
        id: fill.id,
      };
    });
  orderedDecisions.forEach((decision, index) => {
    if (used.has(index)) return;
    rows.push({
      action: decision.action,
      at: decision.barEndedAt,
      decision,
      id: `decision:${decision.barEndedAt}:${String(index)}`,
    });
  });
  return rows.sort((left, right) => left.at.localeCompare(right.at));
}
